import { useEffect, useMemo, useState } from 'react';
import { usePatients } from '@/hooks/usePatients';
import type { Activity, ActivityFilters, ActivityListResponse } from '@/types/activity';
import type { Patient } from '@/types/patient';
import { getPatientFullName } from '@/types/patient';

interface UseDoctorActivityQueryOptions {
  patientIds: string[];
  shouldLoad: boolean;
  loadActivities: (
    patientId: string,
    filters?: ActivityFilters,
    patientName?: string
  ) => Promise<ActivityListResponse>;
}

interface UseDoctorActivityQueryResult {
  patients: Patient[];
  activities: Activity[];
  isLoadingPatients: boolean;
  isLoadingActivities: boolean;
}

export function useDoctorActivityQuery({
  patientIds,
  shouldLoad,
  loadActivities,
}: UseDoctorActivityQueryOptions): UseDoctorActivityQueryResult {
  const { patients, isLoading: isLoadingPatients } = usePatients();
  const [activityCache, setActivityCache] = useState<Record<string, Activity[]>>({});
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  const targetPatients = useMemo(() => {
    if (!shouldLoad) {
      return [];
    }

    if (patientIds.length > 0) {
      return patients.filter((patient) => patientIds.includes(patient.id));
    }

    return patients;
  }, [patientIds, patients, shouldLoad]);

  const missingPatientIds = useMemo(
    () =>
      targetPatients
        .filter((patient) => !activityCache[patient.id])
        .map((patient) => patient.id),
    [targetPatients, activityCache]
  );

  useEffect(() => {
    let cancelled = false;

    async function ensureActivitiesLoaded() {
      if (missingPatientIds.length === 0) {
        return;
      }

      setIsLoadingActivities(true);

      try {
        const missingPatients = targetPatients.filter((patient) =>
          missingPatientIds.includes(patient.id)
        );
        const loaded = await Promise.all(
          missingPatients.map(async (patient) => {
            const response = await loadActivities(
              patient.id,
              undefined,
              getPatientFullName(patient)
            );
            return [patient.id, response.activities] as const;
          })
        );

        if (cancelled) {
          return;
        }

        setActivityCache((current) => {
          const next = { ...current };
          for (const [patientId, activities] of loaded) {
            next[patientId] = activities;
          }
          return next;
        });
      } finally {
        if (!cancelled) {
          setIsLoadingActivities(false);
        }
      }
    }

    void ensureActivitiesLoaded();

    return () => {
      cancelled = true;
    };
  }, [loadActivities, missingPatientIds, targetPatients]);

  const activities = useMemo(() => {
    if (!shouldLoad) {
      return [];
    }

    return targetPatients.flatMap((patient) => activityCache[patient.id] || []);
  }, [shouldLoad, targetPatients, activityCache]);

  return {
    patients,
    activities,
    isLoadingPatients,
    isLoadingActivities,
  };
}
